<template>
	<div class="royal-provision-offer-detail template-layout">
		<OfferHeaderBlock
			:contentData="contentData"
			@closePopup="$emit('closePopup', $event)"
		/>
		<OfferRibbonBlock
			:contentData="contentData"
			class="royal-provision-offer-detail__ribbon"
		/>
		<OfferContentBlock
			:skin="contentData.attributes?.skin"
			class="royal-provision-offer-detail__content"
		>
			<template #content>
				<div class="royal-provision-offer-detail__rewards">
					<template
						v-for="(reward, index) in royalProvisionRewards"
						:key="index"
					>
						<div class="reward-item">
							<div
								v-if="reward.icon"
								class="icon"
								:class="`${reward.icon}-48`"
							/>

							<div class="reward-item__aside">
								<h3>{{ translate(reward.title) }}</h3>
								<p
									v-html="
										replacePlaceholder(
											replacePlaceholder(
												translate(reward.description),
												'{percent}',
												`<b>${reward?.percentage ?? 0}%</b>`,
											),
											'{number}',
											`<b>${reward?.number ?? 0}</b>`,
										)
									"
								/>
							</div>
						</div>

						<HorizontalDecorDivider />
					</template>
				</div>
				<div
					class="royal-provision-offer-detail__state"
					:class="`royal-provision-offer-detail__state--${isCurrentOfferActive ? 'active' : 'default'}`"
				>
					<div
						class="royal-provision-offer-detail__label-text"
						v-if="!isCurrentOfferActive"
					>
						<span
							class="reward-duration-days"
							v-if="contentData.attributes?.reward_duration_days"
							v-html="
								replacePlaceholder(
									translate('ui.activate_for_x_days'),
									'{number}',
									contentData.attributes
										.reward_duration_days_origin
										? `<span class='reward-duration-days-origin'>${contentData.attributes.reward_duration_days_origin}</span><b>${contentData.attributes.reward_duration_days}</b>`
										: `<b>${contentData.attributes.reward_duration_days}</b>`,
								)
							"
						/>
						<Tooltip
							class="royal-provision-offer-detail__label-text-info"
							:placement="Placements.TOP"
						>
							<template #element>
								<AppIconButton
									:type="IconTypes.INFO"
									:size="24"
								/>
							</template>
							<template #tooltip>
								<span>
									{{ translate("ui.end_privileges_info") }}
								</span>
							</template>
						</Tooltip>
					</div>
					<div
						class="royal-provision-offer-detail__timer-wrapper"
						v-else
					>
						<div class="bonus-status">
							{{ translate("event.activated") }}
						</div>
						<div class="royal-provision-offer-detail__timer">
							<Timer
								class="timer"
								:time="currentProvisionRemainingTime"
								:fontSize="18"
								:iconSize="16"
							/>
							<Tooltip :placement="Placements.TOP">
								<template #element>
									<AppIconButton
										:type="IconTypes.INFO"
										:size="24"
									/>
								</template>
								<template #tooltip>
									<span>
										{{
											translate("ui.end_privileges_info")
										}}
									</span>
								</template>
							</Tooltip>
						</div>
					</div>
				</div>
				<div
					v-if="!isCurrentOfferActive"
					class="royal-provision-offer-detail__glow"
				/>
			</template>
		</OfferContentBlock>
		<div
			class="royal-provision-offer-detail__ribbon-red"
			v-if="
				contentData.attributes?.skin &&
				!isCurrentOfferActive &&
				[
					'provision_royal_black_friday_skin_01',
					'provision_royal_black_friday_skin_02',
				].includes(contentData.attributes?.skin)
			"
		>
			2x
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import OfferContentBlock from "@/components/shop/blocks/OfferContentBlock.vue";
import OfferHeaderBlock from "@/components/shop/blocks/OfferHeaderBlock.vue";
import OfferRibbonBlock from "@/components/shop/blocks/OfferRibbonBlock.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import Timer from "@/components/Timer.vue";
import Tooltip from "@/components/Tooltip.vue";
import { royalProvisionRewards } from "@/game/config/shop";
import type { ActiveProvision } from "@/store/useUserStore";
import useUserStore from "@/store/useUserStore";
import { type ShopItem } from "@/types/shop";
import { IconTypes, Placements } from "@/types/ui";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const userStore = useUserStore();
const { activeProvisions } = storeToRefs(userStore);

const isCurrentOfferActive = computed((): boolean => {
	return activeProvisions.value.some(
		(p: ActiveProvision): boolean => p.storeId === contentData.store_id,
	);
});

const currentProvisionRemainingTime = computed((): number => {
	const activeProvision = activeProvisions.value.find(
		(p: ActiveProvision): boolean => p.storeId === contentData.store_id,
	);

	if (!activeProvision?.expiresAt) return 0;

	const expiresAtMs = new Date(activeProvision.expiresAt).getTime();
	if (Number.isNaN(expiresAtMs)) return 0;

	const diffInSeconds = (expiresAtMs - Date.now()) / 1000;
	return Math.max(0, Math.floor(diffInSeconds));
});
</script>

<style lang="scss" scoped>
.royal-provision-offer-detail {
	&__ribbon.offer-ribbon-block {
		margin-top: -1.875rem;

		:deep(.offer-ribbon-block__content) {
			top: 1.875rem;
			padding: 0;
			height: calc(100% - 5rem);

			.offer-ribbon-block__content-text {
				padding-left: 5rem;

				.shrink-text {
					padding-bottom: 0.0625rem;
					text-align: center;
				}
			}
		}
	}

	&__content.offer-content-block {
		margin-top: -3.125rem;
		padding: 2.25rem 0.875rem 1.125rem;
	}

	&__rewards {
		.reward-item {
			@include flex-row-center;
			gap: 1rem;
			padding: 0 2rem;

			.icon {
				flex-shrink: 0;
			}

			&__aside {
				flex: 1 1 auto;

				h3,
				p {
					font-weight: 800;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				}

				h3 {
					color: #fff;
					font-weight: 800;
					font-size: 0.875rem;
				}

				p {
					color: #f2dcb5;
					font-size: 0.75rem;

					:deep(b) {
						color: #19b345;
					}
				}
			}
		}

		.horizontal-decor-divider {
			margin: 0.5rem 0;

			:deep(.cls-1) {
				fill: rgba(244, 210, 120, 0.5);
			}
		}
	}

	&__rewards {
		position: relative;
		z-index: 1;
	}

	&__state {
		position: relative;
		@include flex-row-center;
		z-index: 1;

		&--default {
			padding: 0.75rem 0 0.25rem;
		}
	}

	&__glow {
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 0;
		width: 100%;
		height: 40%;
		@include background(
			url($path-images + "shop/backgrounds/offer-glow-bottom.avif"),
			100% 100%,
			center,
			no-repeat
		);
		opacity: 0.6;
	}

	&__ribbon-red {
		position: absolute;
		bottom: 0.4rem;
		left: -0.75rem;
		z-index: 0;
		width: 4.125rem;
		height: 3.25rem;
		@include background(
			url($path-images + "shop/backgrounds/ribbon-red.avif"),
			contain,
			center,
			no-repeat
		);
		color: #fff3dd;
		@include flex-row-center;
		z-index: 4;
		padding-bottom: 0.8rem;
		font-weight: bold;
		font-size: 1.375rem;
	}

	&__timer-wrapper {
		@include flex-col-center;
		gap: 0.625rem;
		margin-top: 0.25rem;

		.bonus-status {
			position: relative;
			width: 100%;
			@include flex-row-center;
			background-image: linear-gradient(
				to right,
				rgba(0, 82, 24, 0) 0%,
				rgba(0, 82, 24, 0.8) 10%,
				rgba(0, 82, 24, 0.8) 90%,
				rgba(0, 82, 24, 0) 100%
			);
			padding: 0.125rem 2rem 0.25rem;
			color: #f2dcb5;
			font-weight: 800;
			font-size: 1rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;

			&::before {
				position: absolute;
				top: -0.05rem;
				left: 0;
				background: linear-gradient(
					to right,
					rgba(29, 207, 80, 0) 5%,
					rgba(29, 207, 80, 0.8) 20%,
					rgba(29, 207, 80, 0.8) 80%,
					rgba(29, 207, 80, 0) 95%
				);
				width: 100%;
				height: 0.05rem;
				content: "";
			}

			&::after {
				position: absolute;
				bottom: 0;
				left: 0;
				background: linear-gradient(
					to right,
					rgba(29, 207, 80, 0) 5%,
					rgba(29, 207, 80, 0.8) 20%,
					rgba(29, 207, 80, 0.8) 80%,
					rgba(29, 207, 80, 0) 95%
				);
				width: 100%;
				height: 0.05rem;
				content: "";
			}
		}

		.royal-provision-offer-detail__timer {
			@include flex-row-center;
			gap: 0.625rem;
			width: 100%;
		}

		:deep(.timer__time) {
			color: #f2dcb5;
		}
	}

	&__label-text {
		@include flex-row-center;
		color: #f4d278;
		font-weight: 800;
		font-size: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;

		:deep(b) {
			color: #19b345;
		}
		.reward-duration-days {
			:deep(b) {
				color: #fff3dd;
			}
			:deep(.reward-duration-days-origin) {
				position: relative;
				margin-right: 0.25rem;
				&::after {
					position: absolute;
					bottom: 0.25rem;
					left: 0;
					transform: rotate(-25deg);
					transform-origin: bottom left;
					background-color: #f4d278;
					width: 120%;
					height: 0.1rem;
					content: "";
				}
			}
		}

		&-info {
			position: absolute;
			right: 0;
		}
	}
}
</style>
